import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ShieldCheck, X, Phone } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * PhoneVerifySheet (Iter-243) — modal shown INSIDE the post-ad flow when
 * the first-listing 403 `phone_verification_required` is caught. Verifies
 * the user's phone via `POST /auth/phone/request` → `POST /auth/phone/confirm`
 * and calls `onVerified()` to auto-retry the original submit with the
 * untouched draft. Preview env may return `needs_code:false` (SMS off) —
 * we skip straight to retry in that case.
 */
export default function PhoneVerifySheet({ open, onClose, onVerified }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [masked, setMasked] = useState("");
  const phoneRef = useRef(null);
  const codeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Reset every time the sheet opens so previous state doesn't leak.
    setPhone("");
    setCode("");
    setStep("phone");
    setMasked("");
    const t = setTimeout(() => phoneRef.current?.focus(), 30);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const requestMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/auth/phone/request", { phone });
      return data;
    },
    onSuccess: (data) => {
      if (data?.needs_code === false) {
        // Preview env (SMS off) — number saved server-side, gate now passes.
        toast.success("Phone saved — posting your ad…");
        onVerified();
        onClose();
        return;
      }
      setMasked(data?.masked_phone || "");
      setStep("code");
      setTimeout(() => codeRef.current?.focus(), 30);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't send code — check the number and try again."
      );
    },
  });

  const confirmMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/auth/phone/confirm", {
        phone,
        code,
      });
      return data;
    },
    onSuccess: (data) => {
      if (data?.verified) {
        toast.success("Phone verified — posting your ad…");
        onVerified();
        onClose();
      } else {
        toast.error("Verification didn't complete — please try again.");
      }
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Wrong or expired code."
      );
    },
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="phone-verify-backdrop"
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="phone-verify-title"
        data-testid="phone-verify-sheet"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-subtle">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[color:var(--brand-blue)]" />
            <span id="phone-verify-title" className="font-display font-bold text-slate-900">
              Verify your mobile
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="phone-verify-close"
            aria-label="Close"
            className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pt-4 pb-2 text-xs text-slate-500">
          {step === "phone"
            ? "Post your first listing safely — we'll send you a 6-digit code by text. This is one-time; later posts won't ask again."
            : `Enter the 6-digit code we texted to ${masked || phone}.`}
        </div>

        {step === "phone" ? (
          <form
            className="px-5 pb-5 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (requestMut.isPending) return;
              requestMut.mutate();
            }}
          >
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">
                NZ mobile number <span className="text-red-500">*</span>
              </span>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-soft bg-white px-3 focus-within:border-[color:var(--brand-blue)]">
                <Phone className="w-4 h-4 text-slate-400" />
                <input
                  ref={phoneRef}
                  data-testid="phone-verify-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9+ ]/g, "").slice(0, 20))}
                  type="tel"
                  inputMode="tel"
                  placeholder="021 123 4567"
                  className="w-full py-2.5 outline-none border-0 bg-transparent text-sm"
                  required
                />
              </div>
            </label>
            <button
              type="submit"
              disabled={!phone.trim() || requestMut.isPending}
              data-testid="phone-verify-send-code"
              className="w-full rounded-full bg-[color:var(--brand-blue)] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 text-sm inline-flex items-center justify-center gap-2 transition-colors"
            >
              {requestMut.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                "Send code"
              )}
            </button>
          </form>
        ) : (
          <form
            className="px-5 pb-5 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (confirmMut.isPending) return;
              confirmMut.mutate();
            }}
          >
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">
                6-digit code <span className="text-red-500">*</span>
              </span>
              <input
                ref={codeRef}
                data-testid="phone-verify-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                className="mt-1 w-full rounded-xl border border-soft bg-white py-2.5 px-3 text-lg font-mono tracking-widest text-center outline-none focus:border-[color:var(--brand-blue)]"
                required
              />
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  // Clear the OTP so returning to the code step later
                  // starts fresh (iteration_70 RCA — state persisted).
                  setCode("");
                  setStep("phone");
                }}
                data-testid="phone-verify-back"
                className="rounded-full border border-soft bg-white hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2 text-xs"
              >
                Change number
              </button>
              <button
                type="submit"
                disabled={code.length < 6 || confirmMut.isPending}
                data-testid="phone-verify-confirm"
                className="flex-1 rounded-full bg-[color:var(--brand-blue)] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 text-sm inline-flex items-center justify-center gap-2 transition-colors"
              >
                {confirmMut.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
                  </>
                ) : (
                  "Verify & post ad"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
